import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { groupByProximity } from '../../lib/geo';
import { isFirmLike } from '../../lib/orgTypes';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const STYLES = {
  dark: 'mapbox://styles/mapbox/navigation-night-v1',
  light: 'mapbox://styles/yoneldev/cmn6374fs005e01s05j2mdayf',
};

const DEFAULT_CENTER = [-81.5, 27.8]; // Florida
const DEFAULT_ZOOM = 6.5;

const STATE_COORDS = {
  AL:[-86.9,32.3],AK:[-154.5,63.6],AZ:[-111.1,34.0],AR:[-91.8,35.2],CA:[-119.4,36.8],CO:[-105.4,39.1],
  CT:[-73.1,41.6],DE:[-75.5,38.9],FL:[-81.8,27.8],GA:[-83.6,32.9],HI:[-155.6,19.9],ID:[-114.4,44.2],
  IL:[-89.0,40.3],IN:[-86.1,40.3],IA:[-93.2,42.0],KS:[-98.5,39.0],KY:[-84.7,37.7],LA:[-92.0,31.2],
  ME:[-69.4,44.7],MD:[-76.6,39.0],MA:[-71.5,42.2],MI:[-84.5,43.3],MN:[-94.7,46.7],MS:[-89.5,32.7],
  MO:[-91.8,37.9],MT:[-110.4,46.8],NE:[-98.3,41.1],NV:[-116.4,38.8],NH:[-71.5,43.5],NJ:[-74.5,40.3],
  NM:[-106.2,34.8],NY:[-74.5,43.3],NC:[-79.8,35.6],ND:[-99.8,47.5],OH:[-82.9,40.4],OK:[-97.1,35.0],
  OR:[-120.6,43.8],PA:[-77.2,41.2],RI:[-71.5,41.6],SC:[-81.2,33.8],SD:[-99.4,43.9],TN:[-86.6,35.5],
  TX:[-97.6,31.1],UT:[-111.1,39.3],VT:[-72.7,44.6],VA:[-79.4,37.8],WA:[-120.7,47.4],WV:[-80.5,38.5],
  WI:[-88.8,43.8],WY:[-107.6,43.1],DC:[-77.0,38.9],
};

const geocodeCache = Object.create(null);

async function geocodeAddress(org) {
  const address = [org.address_line1, org.city, org.state, org.zip].filter(Boolean).join(', ');
  if (!address) return null;

  if (geocodeCache[address]) return geocodeCache[address];

  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxgl.accessToken}&limit=1`
    );
    const data = await res.json();
    const coords = data.features?.[0]?.center;
    if (coords) geocodeCache[address] = coords;
    return coords || null;
  } catch {
    return null;
  }
}

function getStaticCoords(item) {
  if (item.lat && item.lng) return [Number(item.lng), Number(item.lat)];
  if (item.location?.coordinates) return item.location.coordinates;
  // Intentionally no state-centroid fallback. Pinning an un-geocoded org
  // to the middle of its state ("Florida ≈ Sebring") is worse UX than
  // not pinning it at all — looks like a real location and misleads
  // users (see 2026-06-07 Dream Team Law misplacement). Orgs with null
  // coordinates still appear in the side panel, just without a marker.
  return null;
}

// Same pin expansion as the admin map: an org's LOCATIONS are its pins —
// one per site, however many it has. The org's own coordinates are only a
// fallback for orgs with no geocoded locations (law firms, legacy rows),
// never an extra pin: every org's HQ is also a location row, so pinning
// both drew a bogus "2" group marker on single-site practices.
// Un-geocoded locations get no pin (never a borrowed-coordinate or
// state-centroid stand-in).
function expandToPins(orgs) {
  const out = [];
  for (const org of orgs) {
    const locPins = [];
    for (const loc of (org.locations || []).filter(l => !l.archived_at)) {
      const c = getStaticCoords(loc);
      if (c) locPins.push({ org, location: loc, pinKey: `${org.id}:${loc.id}`, coords: c });
    }
    if (locPins.length) {
      out.push(...locPins);
    } else {
      const orgCoords = getStaticCoords(org);
      if (orgCoords) out.push({ org, location: null, pinKey: org.id, coords: orgCoords });
    }
  }
  return out;
}

// The selectable entry behind a pin: the org itself, or — for a location
// pin — the org overlaid with that location's identity (id `org::loc`,
// real org in _orgId; identical to DirectoryPage's list entries).
function entryFor(pin) {
  const { org, location: l } = pin;
  if (!l) return org;
  return {
    ...org,
    id: `${org.id}::${l.id}`,
    _orgId: org.id,
    _parentName: org.name,
    _isLocation: true,
    name: l.name || org.name,
    address_line1: l.address_line1 || org.address_line1,
    city: l.city || org.city,
    state: l.state || org.state,
    zip: l.zip || org.zip,
    phone: l.phone || org.phone,
    lat: l.lat ?? org.lat,
    lng: l.lng ?? org.lng,
  };
}

function getTheme() {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export default function DirectoryMap({ orgs, selectedOrg, onSelectOrg, typeColors = {}, typeLabels = {}, currentOrgId, currentOrgType, onReferCase, onViewProfile, isClientView, focusLocation }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({}); // keyed by org.id
  const popupRef = useRef(null);
  const onSelectRef = useRef(onSelectOrg);
  onSelectRef.current = onSelectOrg;
  const onViewProfileRef = useRef(onViewProfile);
  onViewProfileRef.current = onViewProfile;
  const groupPopupRef = useRef(null);   // 'N at this address' list popup

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLES[getTheme()],
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    map.on('click', () => {
      onSelectRef.current(null);
      if (groupPopupRef.current) { groupPopupRef.current.remove(); groupPopupRef.current = null; }
    });

    mapRef.current = map;

    return () => { map.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line

  // Theme switching
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (mapRef.current) mapRef.current.setStyle(STYLES[getTheme()]);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Place markers — one per location. When orgs list changes only.
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    (async () => {
      // Clear old markers
      Object.values(markersRef.current).forEach(m => m.marker.remove());
      markersRef.current = {};

      // Same treatment as the admin map: co-located facilities (any org)
      // group into ONE numbered marker whose popup lists every facility
      // there, each individually openable — nothing hides behind anything.
      const allPins = expandToPins(orgs).filter(p => p.coords);
      const groups = groupByProximity(allPins, p => [p.coords[1], p.coords[0]]);

      groups.forEach((group, gi) => {
        if (cancelled) return;
        // Prefer the org-level pin as the group's face, like the admin map
        const pin = group.items.find(p => !p.location) || group.items[0];
        const { org } = pin;
        const coords = [group.lng, group.lat];
        const stacked = group.items.length;

        const color = typeColors[org.type] || '#6B7280';

        // Marker chrome copied verbatim from AdminOrgMap so both maps read
        // identically: every single facility is the same dot whether it is an
        // organization's own address or one of its locations — that
        // distinction is bookkeeping, not something a person browsing the map
        // needs to decode. Co-located facilities still collapse into one
        // numbered badge so nothing hides behind anything.
        const el = document.createElement('div');
        let kind;
        if (stacked > 1) {
          kind = 'stack';
          el.style.cssText = `
            min-width:20px;height:20px;padding:0 5px;border-radius:10px;
            background:${color};border:2.5px solid white;
            box-shadow:0 0 0 3px ${color}40, 0 2px 8px rgba(0,0,0,0.35);
            display:flex;align-items:center;justify-content:center;
            font:700 11px/1 Inter,-apple-system,sans-serif;color:#fff;cursor:pointer;
            transition:box-shadow 0.15s ease;`;
          el.textContent = String(stacked);
          el.title = group.items.map(p => p.location?.name || p.org.name).join('\n');
        } else {
          kind = 'pin';
          el.style.cssText = `
            width:16px;height:16px;border-radius:50%;
            background:${color};border:3px solid white;
            box-shadow:0 0 0 3px ${color}45, 0 2px 8px rgba(0,0,0,0.35);
            cursor:pointer;transition:box-shadow 0.15s ease;`;
          // The tooltip still names the location — same dot, fuller label.
          el.title = pin.location ? `${pin.location.name} — ${org.name}` : org.name;
        }

        // No transform on hover — it breaks Mapbox marker positioning

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat(coords)
          .addTo(mapRef.current);

        el.addEventListener('click', (e) => {
          e.stopPropagation();
          if (groupPopupRef.current) { groupPopupRef.current.remove(); groupPopupRef.current = null; }
          if (stacked === 1) {
            // Select the FACILITY that was clicked. A location pin carries
            // its own name, address and phone (same entry shape as the
            // directory list builds), so the popup never shows the parent
            // org's headquarters for a branch. _clickCoords keeps the
            // camera here, not at another facility.
            onSelectRef.current({ ...entryFor(pin), _clickCoords: coords });
            return;
          }
          // Stacked: list popup exactly like the admin map
          onSelectRef.current(null);
          const isDark = getTheme() === 'dark';
          const t = isDark
            ? { bg: '#14171f', text: '#f0f2f5', muted: '#a0a8be', border: 'rgba(255,255,255,0.08)' }
            : { bg: '#ffffff', text: '#111827', muted: '#6B7280', border: 'rgba(0,0,0,0.08)' };
          const entries = group.items.map((p, idx) => `
            <div style="padding:8px 0;${idx ? `border-top:1px solid ${t.border};` : ''}">
              <div style="font-size:12.5px;font-weight:700;color:${t.text};">${p.location?.name || p.org.name}</div>
              <div style="font-size:10.5px;color:${t.muted};margin-top:1px;">
                ${typeLabels[p.org.type] || p.org.type}${p.location ? ` · part of ${p.org.name}` : ''}
              </div>
              <button id="dir-group-open-${gi}-${idx}" style="margin-top:5px;width:100%;font-size:10.5px;font-weight:600;padding:5px 0;border-radius:7px;color:white;background:linear-gradient(135deg,#0DA7CA,#223368);border:none;cursor:pointer;">View profile</button>
            </div>`).join('');
          groupPopupRef.current = new mapboxgl.Popup({
            closeButton: true, closeOnClick: false, offset: 25, maxWidth: 'none', className: 'directory-popup', focusAfterOpen: false,
          })
            .setLngLat(coords)
            .setHTML(`
              <div style="font-family:Inter,-apple-system,sans-serif;width:240px;background:${t.bg};border-radius:12px;overflow:hidden;border:1px solid ${t.border};box-shadow:0 16px 48px rgba(0,0,0,${isDark ? '0.6' : '0.15'});">
                <div style="height:3px;background:${color};"></div>
                <div style="padding:12px 14px;max-height:320px;overflow-y:auto;">
                  <div style="font-size:11px;font-weight:700;color:${t.muted};text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">${stacked} at this address</div>
                  ${entries}
                </div>
              </div>`)
            .addTo(mapRef.current);
          group.items.forEach((p, idx) => {
            const btn = document.getElementById(`dir-group-open-${gi}-${idx}`);
            if (btn) btn.addEventListener('click', (ev) => {
              ev.stopPropagation(); ev.preventDefault();
              onViewProfileRef.current?.(p.org.id, isClientView && p.org.type === 'medical_practice' ? 'book' : undefined);
            });
          });
        });

        markersRef.current[pin.pinKey] = { marker, el, coords, color, orgId: org.id, kind };
      });
    })();

    return () => { cancelled = true; };
  }, [orgs, typeColors, typeLabels, isClientView]); // No selectedOrg dependency

  // Update marker styles on selection change. All pins for the selected
  // org (across its locations) get highlighted, not just one.
  useEffect(() => {
    // List entries for individual locations carry a composite id with the
    // real org in _orgId — markers are keyed by the real org id.
    const selId = selectedOrg?._orgId || selectedOrg?.id;
    onSelectRef.current._selectedId = selId;

    Object.values(markersRef.current).forEach(({ el, color, orgId, kind }) => {
      const isSel = orgId === selId;
      const c = color || '#6B7280';
      // Highlight = widen the glow ring only; sizes/borders stay identical
      // to the admin map's resting style. One branch for every marker now
      // that locations and organizations share the same dot.
      el.style.boxShadow = `0 0 0 ${isSel ? 8 : 3}px ${c}${isSel ? '70' : kind === 'stack' ? '40' : '45'}, 0 2px 8px rgba(0,0,0,0.35)`;
    });
  }, [selectedOrg]);

  // Fly to an external focus location (search result / Near Me)
  useEffect(() => {
    if (!mapRef.current || !focusLocation) return;
    mapRef.current.flyTo({
      center: [focusLocation.lng, focusLocation.lat],
      zoom: focusLocation.zoom || 10,
      essential: true,
      speed: 1.4,
      curve: 1.6,
    });
  }, [focusLocation?.lat, focusLocation?.lng, focusLocation?.zoom]); // eslint-disable-line

  // Popup on selected org — geocode if needed, then show
  useEffect(() => {
    if (!mapRef.current) return;
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
    if (!selectedOrg) return;
    if (groupPopupRef.current) { groupPopupRef.current.remove(); groupPopupRef.current = null; }

    let cancelled = false;

    (async () => {
      // Get coords, most specific first: the exact marker that was clicked,
      // then the selected entry's own coordinates (list entries carry the
      // facility's lat/lng — a Miami location must never fly to the org's
      // first marker in Tampa), then any marker of this org, then geocode.
      const selId = selectedOrg._orgId || selectedOrg.id;
      let coords = selectedOrg._clickCoords || getStaticCoords(selectedOrg) || null;
      if (!coords) {
        const orgMarkers = Object.values(markersRef.current).filter(m => m.orgId === selId);
        if (orgMarkers.length) {
          const ll = orgMarkers[0].marker.getLngLat();
          coords = [ll.lng, ll.lat];
        } else {
          coords = await geocodeAddress(selectedOrg);
        }
      }

      if (cancelled || !coords || !mapRef.current) return;

      // Camera rules, matching the admin map's feel:
      // - marker already on screen (a map click) → NO camera motion at all;
      //   flyTo's ballistic arc would swoop the view "over the map" even for
      //   a pin right under the cursor. The popup just appears.
      // - off-screen (picked from the list or search) → fly there.
      const inView = mapRef.current.getBounds().contains(coords);
      if (!inView) {
        const currentZoom = mapRef.current.getZoom();
        const targetZoom = currentZoom < 9 ? 10 : Math.max(currentZoom, 10);
        mapRef.current.flyTo({
          center: coords,
          zoom: targetZoom,
          essential: true,
          speed: 1.4,
          curve: 1.6,
        });
      }

      const color = typeColors[selectedOrg.type] || '#6B7280';
      const label = typeLabels[selectedOrg.type] || selectedOrg.type;
      const initials = selectedOrg.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || '??';
      const addr = [selectedOrg.address_line1, selectedOrg.city, selectedOrg.state].filter(Boolean).join(', ');
      const isDark = getTheme() === 'dark';
      const t = isDark
        ? { bg: '#14171f', text: '#f0f2f5', muted: '#a0a8be', border: 'rgba(255,255,255,0.06)', link: '#30C0DD', specBg: 'rgba(13, 167, 202,0.12)', specText: '#30C0DD' }
        : { bg: '#ffffff', text: '#111827', muted: '#6B7280', border: 'rgba(0,0,0,0.06)', link: '#0DA7CA', specBg: 'rgba(13, 167, 202,0.06)', specText: '#0DA7CA' };

      const specs = (selectedOrg.specialties || []).slice(0, 3).map(s =>
        `<span style="font-size:10px;padding:2px 8px;border-radius:8px;background:${t.specBg};color:${t.specText};">${s}</span>`
      ).join(' ');

      // Who's looking decides what they can do: patients book, law firms
      // refer a case, clinics (and firms looking at firms) just see the
      // profile. Partnership requests are gone from here — the first
      // referral is the introduction.
      const viewerIsFirm = isFirmLike(currentOrgType);
      const targetIsMedical = selectedOrg.type === 'medical_practice';
      const actions = [
        isClientView && targetIsMedical && `<a href="#" id="book-link-${selectedOrg.id}" style="color:${t.link};text-decoration:none;font-weight:600;font-size:11px;">Book Appointment</a>`,
        `<a href="#" id="profile-link-${selectedOrg.id}" style="color:${t.link};text-decoration:none;font-weight:600;font-size:11px;">View Profile</a>`,
        selectedOrg.email && `<a href="mailto:${selectedOrg.email}" style="color:${t.link};text-decoration:none;font-weight:600;font-size:11px;">Email</a>`,
      ].filter(Boolean).join(`<span style="color:${t.muted};margin:0 6px;">·</span>`);

      // Bottom action button
      let partnershipHtml = '';
      if (currentOrgId && selId !== currentOrgId) {
        const ps = selectedOrg.partnership;
        const partnerBadge = ps?.partnership_status === 'accepted'
          ? `<span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;padding:4px 12px;border-radius:8px;color:#10B981;background:rgba(16,185,129,0.1);margin-bottom:8px;"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Connected Partner</span>`
          : '';
        if (isClientView && targetIsMedical) {
          partnershipHtml = `<div style="padding:8px 16px 12px;border-top:1px solid ${t.border};"><button id="book-appt-${selectedOrg.id}" style="font-size:11px;font-weight:600;padding:8px 14px;border-radius:8px;color:white;background:linear-gradient(135deg,#0DA7CA,#223368);border:none;cursor:pointer;width:100%;">Book Appointment</button></div>`;
        } else if (viewerIsFirm && targetIsMedical) {
          partnershipHtml = `<div style="padding:8px 16px 12px;border-top:1px solid ${t.border};">${partnerBadge}<button id="refer-case-${selectedOrg.id}" style="font-size:11px;font-weight:600;padding:8px 14px;border-radius:8px;color:white;background:linear-gradient(135deg,#0DA7CA,#223368);border:none;cursor:pointer;width:100%;">Refer a case</button><div style="font-size:10px;color:${t.muted};margin-top:6px;text-align:center;">Pick the client, the packet assembles from the file, the clinic has an hour to accept.</div></div>`;
        } else if (partnerBadge) {
          partnershipHtml = `<div style="padding:8px 16px 12px;border-top:1px solid ${t.border};">${partnerBadge}</div>`;
        }
      }

      const html = `
        <div style="font-family:Inter,-apple-system,sans-serif;width:260px;background:${t.bg};border-radius:14px;overflow:hidden;box-shadow:0 16px 48px rgba(0,0,0,${isDark ? '0.6' : '0.15'});border:1px solid ${t.border};">
          <div style="height:4px;background:${color};"></div>
          <div style="padding:14px 16px;border-bottom:1px solid ${t.border};">
            <div style="display:flex;align-items:center;gap:10px;">
              ${selectedOrg.logo_url
                ? `<img src="${selectedOrg.logo_url}" style="width:38px;height:38px;border-radius:10px;object-fit:cover;"/>`
                : `<div style="width:38px;height:38px;border-radius:10px;background:${color};display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:white;">${initials}</div>`
              }
              <div style="min-width:0;flex:1;">
                <div style="font-size:14px;font-weight:700;color:${t.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${selectedOrg.name}</div>
                ${selectedOrg._isLocation && selectedOrg._parentName && selectedOrg._parentName !== selectedOrg.name ? `<div style="font-size:10.5px;color:${t.muted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">part of ${selectedOrg._parentName}</div>` : ''}
                <span style="display:inline-block;font-size:9px;font-weight:600;padding:2px 7px;border-radius:8px;color:white;background:${color};margin-top:3px;">${label}</span>
              </div>
            </div>
          </div>
          <div style="padding:12px 16px 14px;">
            ${addr ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr + (selectedOrg.zip ? ' ' + selectedOrg.zip : ''))}" target="_blank" rel="noopener noreferrer" style="display:block;font-size:11px;color:${t.link};margin-bottom:3px;text-decoration:none;"><span style="text-decoration:underline;">${addr}${selectedOrg.zip ? ' ' + selectedOrg.zip : ''}</span></a>` : ''}
            ${selectedOrg.phone ? `<div style="font-size:11px;color:${t.muted};margin-bottom:8px;">${selectedOrg.phone}</div>` : ''}
            ${specs ? `<div style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:10px;">${specs}</div>` : ''}
            ${actions ? `<div style="padding-top:8px;border-top:1px solid ${t.border};">${actions}</div>` : ''}
          </div>
          ${partnershipHtml}
        </div>
      `;

      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25,
        maxWidth: 'none',
        className: 'directory-popup',
        focusAfterOpen: false,
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(mapRef.current);

      // Attach click handlers for popup actions
      const referBtn = document.getElementById(`refer-case-${selectedOrg.id}`);
      if (referBtn && onReferCase) {
        referBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); onReferCase(selId); });
      }
      // Bottom button (client: book, staff: partnership)
      const bookBtn = document.getElementById(`book-appt-${selectedOrg.id}`);
      if (bookBtn && onViewProfile) {
        bookBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); onViewProfile(selId, 'book'); });
      }
      // Action row links
      const bookLink = document.getElementById(`book-link-${selectedOrg.id}`);
      if (bookLink && onViewProfile) {
        bookLink.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); onViewProfile(selId, 'book'); });
      }
      const profileLink = document.getElementById(`profile-link-${selectedOrg.id}`);
      if (profileLink && onViewProfile) {
        profileLink.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); onViewProfile(selId); });
      }
      const profileBtn = document.getElementById(`view-profile-${selectedOrg.id}`);
      if (profileBtn && onViewProfile) {
        profileBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); onViewProfile(selId); });
      }

    })();

    return () => { cancelled = true; };
  }, [selectedOrg, typeColors, typeLabels, currentOrgId, currentOrgType, onReferCase, onViewProfile]);

  // Inject popup styles
  useEffect(() => {
    const s = document.createElement('style');
    s.textContent = `
      .directory-popup .mapboxgl-popup-content{background:transparent!important;box-shadow:none!important;padding:0!important;border-radius:14px!important}
      .directory-popup .mapboxgl-popup-tip{display:none!important}
    `;
    document.head.appendChild(s);
    return () => s.remove();
  }, []);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
}
